import * as React from "react";
import type { ParseKeys } from "i18next";
import { MapPin, Map, Boxes, Hexagon, Download, Settings2, Bell } from "lucide-react";
import { MACHINES_URL } from "@/machines/routes";
import { MAP_URL } from "@/map/config/map-urls";
import { ALERTS_URL } from "@/alerts/config/alerts-urls";
import { ML_EXPORTS_URL } from "@/ml-exports/config/ml-exports-urls";
import { SETTINGS_URL } from "@/settings/config/settings-urls";

/** A `common` key: the nav is registered at import time and rendered later. */
export type SidebarNavKey = ParseKeys<"common">;

export interface SidebarNavItem {
  titleKey: SidebarNavKey;
  url: string;
  icon?: React.ElementType;
  /** Optional component rendered as a trailing badge (e.g. a live count). */
  badge?: React.ElementType;
  items?: { titleKey: SidebarNavKey; url: string }[];
}

export interface SidebarNavGroup {
  labelKey: SidebarNavKey;
  items: SidebarNavItem[];
}

// Grouped, labeled sections. URLs are sourced from each module's *_URL
// constants so the nav stays in sync with the route tree. Titles are `common`
// translation keys, resolved by DashboardLayout. "Locations" is the
// app root ("/"). The "Users" settings entry has no dedicated URL constant
// (the route is `user/:id?` under the settings base), so its absolute path is
// composed from SETTINGS_URL.ROOT.
// The Alerts workspace ships Queue, History and Analytics; ingest monitoring
// is a separate ticket and has no entry yet.
export const SIDEBAR_NAV: SidebarNavGroup[] = [
  {
    labelKey: "sidebar.groups.app",
    items: [
      { titleKey: "sidebar.items.locations", url: MACHINES_URL.LOCATION, icon: MapPin },
      { titleKey: "sidebar.items.map", url: MAP_URL.ROOT, icon: Map },
      {
        titleKey: "sidebar.items.alerts",
        url: ALERTS_URL.QUEUE,
        icon: Bell,
        items: [
          { titleKey: "sidebar.items.alertsQueue", url: ALERTS_URL.QUEUE },
          { titleKey: "sidebar.items.alertsHistory", url: ALERTS_URL.HISTORY },
          { titleKey: "sidebar.items.alertsAnalytics", url: ALERTS_URL.ANALYTICS },
        ],
      },
    ],
  },
  {
    labelKey: "sidebar.groups.ai",
    items: [
      {
        titleKey: "sidebar.items.mlExport",
        url: ML_EXPORTS_URL.REVIEW,
        icon: Boxes,
        items: [
          { titleKey: "sidebar.items.mlExportReview", url: ML_EXPORTS_URL.REVIEW },
          { titleKey: "sidebar.items.mlExportConfigs", url: ML_EXPORTS_URL.CONFIGS },
          { titleKey: "sidebar.items.mlExportRuns", url: ML_EXPORTS_URL.RUNS },
        ],
      },
    ],
  },
  {
    labelKey: "sidebar.groups.data",
    items: [
      { titleKey: "sidebar.items.polygons", url: MACHINES_URL.POLYGONS, icon: Hexagon },
      { titleKey: "sidebar.items.export", url: MACHINES_URL.EXPORT, icon: Download },
    ],
  },
  {
    labelKey: "sidebar.groups.system",
    items: [
      {
        titleKey: "sidebar.items.settings",
        url: SETTINGS_URL.ROOT,
        icon: Settings2,
        items: [
          { titleKey: "sidebar.items.settingsOrganization", url: SETTINGS_URL.ORGANIZATION },
          { titleKey: "sidebar.items.settingsUsers", url: `${SETTINGS_URL.ROOT}/user` },
          { titleKey: "sidebar.items.settingsLocations", url: SETTINGS_URL.LOCATION },
          { titleKey: "sidebar.items.settingsMachines", url: SETTINGS_URL.MACHINE },
          { titleKey: "sidebar.items.settingsTaxonomy", url: SETTINGS_URL.TAXONOMY },
        ],
      },
    ],
  },
];
